import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  IoAttachOutline,
  IoDocumentTextOutline,
  IoVideocamOutline,
  IoClose,
  IoCheckmarkCircleOutline,
  IoArrowBack,
  IoBookOutline,
  IoChevronDown,
} from "react-icons/io5";
import HttpApis from "../../services/HttpApis";
import Footer from "../Footer/Footer";
import { Answer } from "./HelpCenter";
import { faqsForTopic } from "./helpFaqData";
import "./HelpCenter.css";

// Limits mirror the backend (viewerSupport.validator.js, supportAttachments.js).
const SUBJECT_MAX = 150;
const MESSAGE_MIN = 10;
const MESSAGE_MAX = 5000;
const ATTACH_MAX_FILES = 3;
const ATTACH_MAX_MB = 10; // images and PDFs
const ATTACH_VIDEO_MAX_MB = 100;
// mp4, .mov (iPhone/Mac screen recordings), webm (browser screen capture)
const ATTACH_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];
const ATTACH_TYPES = ["image/jpeg", "image/png", "application/pdf", ...ATTACH_VIDEO_TYPES];
const isVideo = (file) => ATTACH_VIDEO_TYPES.includes(file.type);
const OTHER = "other";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Used only if GET client/v1/support/options fails.
const FALLBACK_OPTIONS = {
  categories: [
    { key: "login", label: "Can't sign in or keep getting signed out" },
    { key: "activation", label: "Activating a TV or device" },
    { key: "playback", label: "Video won't play or keeps buffering" },
    { key: "subscription", label: "Subscription or plan question" },
    { key: "billing", label: "Payment, charge or refund" },
    { key: "account", label: "Account or profile" },
    { key: OTHER, label: "Other" },
  ],
  platforms: ["Website", "Roku", "Fire TV", "Android TV / Google TV", "iPhone / iPad", "Android phone / tablet", "Other"],
};

// Placeholder hints so viewers include the details that help.
const HINTS = {
  login: "Which email do you sign in with, and what message do you see?",
  activation: "Which device are you activating, and what happens when you enter the code?",
  playback: "Which title, which device, and roughly when did it happen?",
  av_quality: "Which title, and what's wrong: picture, sound or subtitles?",
  app: "Which device and app version? What were you doing when it crashed?",
  subscription: "Which plan, and where did you subscribe (this website, Roku, Google Play...)?",
  billing: "The date and amount of the charge, and what looks wrong.",
  purchase: "Which title did you buy or rent, and what's the problem?",
  content: "Which show or movie are you looking for?",
  account: "What were you trying to change, and what happened?",
};
const DEFAULT_HINT = "Tell us what happened, what you expected, and the steps to see the problem.";

const fmtSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// Best guess of where the viewer is, so the Device dropdown starts sensible.
const guessPlatform = () => {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "iPhone / iPad";
  if (/Android/i.test(ua)) return "Android phone / tablet";
  return "Website";
};

// Help Center answers for the chosen category, shown above the rest of the
// form so the viewer can solve it without sending a ticket.
const RelatedFaqs = ({ items }) => {
  const [openIdx, setOpenIdx] = useState(null);
  if (!items.length) return null;
  return (
    <div className="hc-related">
      <p className="hc-related-title">Is this your problem?</p>
      <ul className="hc-list">
        {items.map((item, i) => {
          const open = openIdx === i;
          return (
            <li key={item.q} className={`hc-item ${open ? "hc-item--open" : ""}`}>
              <button
                type="button"
                className="hc-question"
                onClick={() => setOpenIdx(open ? null : i)}
                aria-expanded={open}
              >
                <span>{item.q}</span>
                <IoChevronDown className="hc-chev" />
              </button>
              {open && (
                <div className="hc-answer">
                  <Answer parts={item.a} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <p className="hc-related-foot">Still stuck? Fill in the rest of the form and we'll help.</p>
    </div>
  );
};

/**
 * Viewer "Contact Support" form -> POST client/v1/support. Works signed in
 * (we reply to the account email) or signed out (name + email asked for),
 * since not being able to sign in is a common reason to write.
 */
const ContactSupport = () => {
  const location = useLocation();
  const token = useSelector((state) => state.authReducer?.token) || HttpApis.getAuthToken();
  const account = useSelector((state) => state.authReducer?.user?.data?.user);
  const signedIn = Boolean(token);

  const [options, setOptions] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [platform, setPlatform] = useState(guessPlatform);
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [files, setFiles] = useState([]); // [{ id, file, preview }]
  const [dragging, setDragging] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null); // { id, email }
  const fileInputRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let alive = true;
    HttpApis.get("client/v1/support/options")
      .then((res) => {
        const data = res?.data?.data;
        if (alive) setOptions(data?.categories?.length ? data : FALLBACK_OPTIONS);
      })
      .catch(() => alive && setOptions(FALLBACK_OPTIONS));
    return () => {
      alive = false;
    };
  }, []);

  // Revoke image previews on unmount.
  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(() => () => filesRef.current.forEach((f) => f.preview && URL.revokeObjectURL(f.preview)), []);

  const clearError = (key) => errors[key] && setErrors((p) => ({ ...p, [key]: undefined }));

  const addFiles = (incoming) => {
    const list = Array.from(incoming || []);
    if (!list.length) return;
    let error;
    const accepted = [];
    for (const file of list) {
      const maxMb = isVideo(file) ? ATTACH_VIDEO_MAX_MB : ATTACH_MAX_MB;
      if (!ATTACH_TYPES.includes(file.type)) error = `${file.name}: only JPG, PNG, PDF, MP4, MOV or WEBM files`;
      else if (file.size > maxMb * 1024 * 1024) error = `${file.name} is larger than ${maxMb}MB`;
      else if (files.length + accepted.length >= ATTACH_MAX_FILES) error = `You can attach up to ${ATTACH_MAX_FILES} files`;
      else
        accepted.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
          file,
          preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
        });
    }
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
    setErrors((p) => ({ ...p, attachments: error }));
  };

  const removeFile = (id) => {
    const gone = files.find((f) => f.id === id);
    if (gone?.preview) URL.revokeObjectURL(gone.preview);
    setFiles((prev) => prev.filter((f) => f.id !== id));
    clearError("attachments");
  };

  const handlePaste = (e) => {
    const pasted = Array.from(e.clipboardData?.files || []);
    if (!pasted.length || sending) return;
    e.preventDefault();
    addFiles(pasted);
  };

  const selected = (options?.categories || []).find((c) => c.key === category);
  const subject = category === OTHER ? customSubject.trim() : selected?.label || "";

  const validate = () => {
    const next = {};
    if (!signedIn) {
      if (!name.trim()) next.name = "Please enter your name";
      if (!email.trim()) next.email = "Please enter your email so we can reply";
      else if (!EMAIL_RE.test(email.trim())) next.email = "Please enter a valid email address";
    }
    if (!category) next.category = "Please choose what this is about";
    else if (category === OTHER && !customSubject.trim()) next.subject = "Please add a short subject";
    if (!message.trim()) next.message = "Please describe the problem";
    else if (message.trim().length < MESSAGE_MIN)
      next.message = `Please add a bit more detail (at least ${MESSAGE_MIN} characters)`;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending || !validate()) return;
    setSending(true);
    setFormError("");
    try {
      const form = new FormData();
      if (!signedIn) {
        form.append("name", name.trim());
        form.append("email", email.trim());
      }
      form.append("category", category);
      form.append("subject", subject);
      form.append("platform", platform);
      form.append("message", message.trim());
      // Page the viewer came from (the footer link passes it).
      const fromPage = String(location.state?.from || "").slice(0, 300);
      if (fromPage) form.append("page", fromPage);
      if (website) form.append("website", website);
      files.forEach((f) => form.append("attachments", f.file));
      // HttpApis defaults to JSON, which would turn FormData into JSON and
      // drop the files; let axios set the multipart boundary instead.
      const res = await HttpApis.post("client/v1/support", form, { "Content-Type": "multipart/form-data" });
      files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
      setFiles([]);
      setSent({ id: res?.data?.data?.ticket_id, email: signedIn ? account?.email : email.trim() });
    } catch (err) {
      // Request.js rejects with the response body ({ message, errors }).
      const fieldErrors = err?.errors || {};
      const mapped = {};
      Object.entries(fieldErrors).forEach(([k, v]) => {
        const key = k === "subject" && category !== OTHER ? "category" : k;
        mapped[key] = Array.isArray(v) ? v[0] : v;
      });
      if (Object.keys(mapped).length) setErrors(mapped);
      setFormError(err?.message || "Couldn't send your message. Please check your connection and try again.");
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setSent(null);
    setCategory("");
    setCustomSubject("");
    setMessage("");
    setErrors({});
    setFormError("");
  };

  return (
    <div className="hc-page">
      <div className="hc-logo-container">
        <Link to="/home">
          <img
            src={`${process.env.PUBLIC_URL}/${process.env.REACT_APP_APP_LOGO}`}
            alt="Logo"
            className="hc-logo"
          />
        </Link>
      </div>

      <main className="hc-main hc-main--narrow">
        <Link to="/help-center" className="hc-back">
          <IoArrowBack /> Help Center
        </Link>
        <header className="hc-hero hc-hero--left">
          <h1 className="hc-title">Contact Support</h1>
          <p className="hc-subtitle">
            Tell us what's going on and we'll reply by email
            {signedIn && account?.email ? (
              <>
                {" "}to <strong>{account.email}</strong>
              </>
            ) : null}
            .
          </p>
        </header>

        {!sent && (
          <div className="hc-faq-note">
            <IoBookOutline className="hc-faq-note-icon" />
            <p>
              <strong>Before raising a ticket,</strong> check our{" "}
              <Link to="/help-center" className="hc-inline-link">
                Help Center
              </Link>
              . Most common problems already have a quick answer there.
            </p>
          </div>
        )}

        <div className="hc-form-card">
          {sent ? (
            <div className="hc-success">
              <IoCheckmarkCircleOutline className="hc-success-icon" />
              <h2 className="hc-success-title">Message sent</h2>
              <p className="hc-success-text">
                {sent.id ? `Request #${sent.id}. ` : ""}
                Thanks for reaching out. We'll reply to {sent.email || "your email"}.
              </p>
              <div className="hc-success-actions">
                <Link to="/home" className="hc-btn">
                  Back to watching
                </Link>
                <button type="button" className="hc-btn hc-btn--outline" onClick={resetForm}>
                  Send another message
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} onPaste={handlePaste} noValidate>
              {!signedIn && (
                <div className="hc-row">
                  <div className="hc-field">
                    <label htmlFor="cs-name" className="hc-label">
                      Your name
                    </label>
                    <input
                      id="cs-name"
                      className={`hc-input ${errors.name ? "hc-input--error" : ""}`}
                      value={name}
                      maxLength={255}
                      autoComplete="name"
                      onChange={(e) => {
                        setName(e.target.value);
                        clearError("name");
                      }}
                      disabled={sending}
                    />
                    <span className="hc-error">{errors.name}</span>
                  </div>
                  <div className="hc-field">
                    <label htmlFor="cs-email" className="hc-label">
                      Email
                    </label>
                    <input
                      id="cs-email"
                      type="email"
                      className={`hc-input ${errors.email ? "hc-input--error" : ""}`}
                      value={email}
                      maxLength={255}
                      autoComplete="email"
                      placeholder="The email on your account, if you have one"
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearError("email");
                      }}
                      disabled={sending}
                    />
                    <span className="hc-error">{errors.email}</span>
                  </div>
                </div>
              )}

              <div className="hc-row">
                <div className="hc-field">
                  <label htmlFor="cs-category" className="hc-label">
                    What's this about?
                  </label>
                  <select
                    id="cs-category"
                    className={`hc-input hc-select ${errors.category ? "hc-input--error" : ""} ${
                      category ? "" : "hc-select--placeholder"
                    }`}
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value);
                      clearError("category");
                      clearError("subject");
                    }}
                    disabled={sending || !options}
                  >
                    <option value="" disabled>
                      {options ? "Choose a topic" : "Loading…"}
                    </option>
                    {(options?.categories || []).map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <span className="hc-error">{errors.category}</span>
                </div>
                <div className="hc-field">
                  <label htmlFor="cs-platform" className="hc-label">
                    Device
                  </label>
                  <select
                    id="cs-platform"
                    className={`hc-input hc-select ${errors.platform ? "hc-input--error" : ""}`}
                    value={platform}
                    onChange={(e) => {
                      setPlatform(e.target.value);
                      clearError("platform");
                    }}
                    disabled={sending || !options}
                  >
                    {(options?.platforms || FALLBACK_OPTIONS.platforms).map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                  <span className="hc-error">{errors.platform}</span>
                </div>
              </div>

              <RelatedFaqs key={category} items={faqsForTopic(category)} />

              {category === OTHER && (
                <div className="hc-field">
                  <label htmlFor="cs-subject" className="hc-label">
                    Subject
                  </label>
                  <input
                    id="cs-subject"
                    className={`hc-input ${errors.subject ? "hc-input--error" : ""}`}
                    value={customSubject}
                    maxLength={SUBJECT_MAX}
                    placeholder="A few words about the problem"
                    onChange={(e) => {
                      setCustomSubject(e.target.value);
                      clearError("subject");
                    }}
                    disabled={sending}
                  />
                  <span className="hc-error">{errors.subject}</span>
                </div>
              )}

              <div className="hc-field">
                <label htmlFor="cs-message" className="hc-label">
                  Message
                </label>
                <textarea
                  id="cs-message"
                  className={`hc-input hc-textarea ${errors.message ? "hc-input--error" : ""}`}
                  placeholder={HINTS[category] || DEFAULT_HINT}
                  value={message}
                  maxLength={MESSAGE_MAX}
                  rows={7}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    clearError("message");
                  }}
                  disabled={sending}
                />
                <div className="hc-meta">
                  <span className="hc-error">{errors.message}</span>
                  <span className="hc-counter">
                    {message.length}/{MESSAGE_MAX}
                  </span>
                </div>
              </div>

              <div className="hc-field">
                <span className="hc-label">
                  Screenshots or video <span className="hc-optional">(optional)</span>
                </span>
                {files.length < ATTACH_MAX_FILES && (
                  <button
                    type="button"
                    className={`hc-dropzone ${dragging ? "hc-dropzone--active" : ""} ${
                      errors.attachments ? "hc-input--error" : ""
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (!dragging) setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragging(false);
                      if (!sending) addFiles(e.dataTransfer.files);
                    }}
                    disabled={sending}
                  >
                    <IoAttachOutline className="hc-dropzone-icon" />
                    <span className="hc-dropzone-text">
                      <strong>Add a screenshot or a screen recording</strong>
                      <span>
                        Drag &amp; drop, click to browse, or paste. JPG, PNG or PDF up to {ATTACH_MAX_MB}MB, MP4, MOV
                        or WEBM video up to {ATTACH_VIDEO_MAX_MB}MB. Up to {ATTACH_MAX_FILES} files.
                      </span>
                    </span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ATTACH_TYPES.join(",")}
                  multiple
                  hidden
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                {files.length > 0 && (
                  <ul className="hc-files">
                    {files.map((f) => (
                      <li key={f.id} className="hc-file">
                        {f.preview ? (
                          <img src={f.preview} alt="" className="hc-file-thumb" />
                        ) : (
                          <span className="hc-file-thumb">
                            {isVideo(f.file) ? <IoVideocamOutline /> : <IoDocumentTextOutline />}
                          </span>
                        )}
                        <span className="hc-file-info">
                          <span className="hc-file-name">{f.file.name}</span>
                          <span className="hc-file-size">{fmtSize(f.file.size)}</span>
                        </span>
                        <button
                          type="button"
                          className="hc-file-remove"
                          onClick={() => removeFile(f.id)}
                          disabled={sending}
                          aria-label={`Remove ${f.file.name}`}
                        >
                          <IoClose />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <span className="hc-error">{errors.attachments}</span>
              </div>

              {/* Honeypot: hidden from people, filled in by bots. */}
              <input
                type="text"
                name="website"
                className="hc-hp"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />

              {formError && <div className="hc-form-error">{formError}</div>}

              <div className="hc-actions">
                <button type="submit" className="hc-btn" disabled={sending}>
                  {sending ? (files.some((f) => isVideo(f.file)) ? "Uploading video…" : "Sending…") : "Send message"}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      <Footer />
      <div className="hc-footer-copy">© {new Date().getFullYear()} All Rights Reserved.</div>
    </div>
  );
};

export default ContactSupport;
